import React, { useState, useMemo, useRef } from 'react';
import ChartBase from './ChartBase';

const TableChart = ({ data, title, subtitle, config }) => {
  const columns = config.columns || [];
  const sortable = config.sortable || false;
  const searchable = config.searchable || false;
  const initialSortBy = useRef(config.sortBy);
  const initialSortDirection = useRef(config.sortDirection);
  const [sortField, setSortField] = useState(initialSortBy.current || null);
  const [sortDirection, setSortDirection] = useState(initialSortDirection.current || 'asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const rowsPerPage = config.rowsPerPage || 20;

  const handleSort = (field) => {
    if (sortable) {
      const direction = sortField === field 
        ? (sortDirection === 'asc' ? 'desc' : 'asc')
        : 'asc';
      setSortField(field);
      setSortDirection(direction);
      setCurrentPage(1);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const extractText = (val) => {
    if (React.isValidElement(val)) {
      const children = val.props?.children;
      if (children != null) {
        return String(children);
      }
      return '';
    }
    return String(val || '');
  };

  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm) return data;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return data.filter(row => {
      return columns.some(col => {
        const value = row[col.key];
        const text = extractText(value);
        return text.toLowerCase().includes(lowerSearchTerm);
      });
    });
  }, [data, searchable, searchTerm, columns]);

  const sortedData = useMemo(() => {
    if (!sortable || !sortField) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      // Handle undefined/null values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      
      const aText = extractText(aVal);
      const bText = extractText(bVal);
      
      const aNum = Number(aText);
      const bNum = Number(bText);
      const aIsNum = !isNaN(aNum) && isFinite(aNum);
      const bIsNum = !isNaN(bNum) && isFinite(bNum);
      
      let comparison = 0;
      if (aIsNum && bIsNum) {
        // Both are numbers - sort numerically
        comparison = aNum - bNum;
      } else {
        // At least one is not a number - sort as strings (case-insensitive)
        const aStr = String(aText).toLowerCase();
        const bStr = String(bText).toLowerCase();
        comparison = aStr.localeCompare(bStr);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortable, sortField, sortDirection]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <ChartBase title={title} subtitle={subtitle}>
      {searchable && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-4 py-2 rounded theme-border theme-bg theme-primary focus:outline-none focus:ring-2 focus:ring-[var(--theme-accent)]"
          />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y theme-border">
          <thead className="theme-bg">
            <tr>
              {columns.map((col, index) => (
                <th
                  key={index}
                  onClick={() => handleSort(col.key)}
                  style={sortable ? { cursor: 'pointer' } : {}}
                  data-sort-direction={sortable && sortField === col.key ? sortDirection : null}
                  className="px-6 py-3 text-left text-xs font-medium theme-primary opacity-70 uppercase tracking-wider hover:bg-[var(--theme-accent)] hover:text-[var(--theme-accent-contrast)]"
                >
                  {col.label}
                  {sortable && sortField === col.key && (
                    <span className="ml-1">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="theme-bg divide-y theme-border">
            {paginatedData.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-[var(--theme-accent)] hover:text-[var(--theme-accent-contrast)]">
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm theme-primary">
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-6 py-3 theme-bg theme-border rounded">
          <div className="text-sm theme-secondary">
            Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, sortedData.length)} of {sortedData.length} entries
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm rounded theme-border theme-primary hover:bg-[var(--theme-accent)] hover:text-[var(--theme-accent-contrast)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm theme-primary">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm rounded theme-border theme-primary hover:bg-[var(--theme-accent)] hover:text-[var(--theme-accent-contrast)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </ChartBase>
  );
};

export default TableChart;
